"use client";

import { Field } from "@/components/tool-kit/primitives";
import { INPUT_MONO, LABEL } from "@/components/tool-kit/styles";
import { parseColor } from "@/lib/tool-kit/color-charts/color-parse";
import { toHex } from "@/lib/tool-kit/color-charts/color-space";

/**
 * A colour typed as text, with a native picker beside it. The text is the source of
 * truth (so `rgb(…)`, `hsl(…)` and names all work); the picker reflects it and, when
 * moved, writes a `#rrggbb` code back. A half-typed value keeps the picker on the last
 * colour it could read.
 */
export function ColorField({
  id,
  label,
  pickerLabel,
  value,
  fallback,
  onChange,
  invalid,
}: {
  id: string;
  label: string;
  /** Accessible name for the picker, e.g. "Pick the text color". */
  pickerLabel: string;
  value: string;
  /** What the picker shows when `value` cannot be read. */
  fallback: string;
  onChange: (value: string) => void;
  /** Shown under the field when the text is not a colour. */
  invalid: string;
}) {
  const parsed = parseColor(value);
  return (
    <div className="space-y-2">
      <Field id={id} label={label}>
        <div className="flex items-center gap-3">
          <input
            aria-invalid={parsed ? undefined : true}
            className={INPUT_MONO}
            id={id}
            onChange={(event) => onChange(event.target.value)}
            spellCheck={false}
            type="text"
            value={value}
          />
          <input
            aria-label={pickerLabel}
            className="h-10 w-14 shrink-0 cursor-pointer rounded-lg border border-border bg-card"
            onChange={(event) => onChange(event.target.value)}
            type="color"
            value={parsed ? toHex(parsed) : fallback}
          />
        </div>
      </Field>
      {parsed ? null : (
        <p className={`${LABEL} text-destructive`} role="alert">
          {invalid}
        </p>
      )}
    </div>
  );
}
